import React, { useState } from 'react';
import PracticeAreaGraphList from '../../shared/components/practiceAreaGraphList/PracticeAreaGraphList';
import { ReportInfoPanel } from '../../shared/components/ReportInfoPanel';
import { useQuery, useReactiveVar } from '@apollo/client';
import GET_APPRAISAL_VIEW from '../queries/getAppraisalViewQuery';
import { Page } from '../../layout';
import ApolloError from '../../shared/components/ApolloError';
import { appraisalViewCustomizationReports } from '../appraisalViewCache';
import JSZip from 'jszip';
import { getPracticeAreaCharacterizationCSV } from '../../industryReports/reportDownloads/practiceAreaCharacterizationReport';
import { getAggregatePercentageCSV } from '../../industryReports/reportDownloads/aggregatePercentageReport';
import IndustryReportsDescriptions from '../../industryReports/components/IndustryReportsDescriptions';
import { saveAs } from 'file-saver';
import PageInstructions from '../../shared/components/PageInstructions';
import { Breadcrumb } from '../../shared/components/breadcrumb/Breadcrumb';
import { systemRole } from '../../../apollo/cache';
import { getAdjustedBreadcrumbPathItems } from '../../shared/services/breadcrumbService';

const AppraisalViewPage = () => {
  const requestedReports = useReactiveVar(appraisalViewCustomizationReports).map(request => ({
    includeHigherLevels: request.includeHigherLevels,
    includeHigherProgramYears: request.includeHigherProgramYears,
    includeExactMatchedDeviceClasses: request.includeExactMatchedDeviceClasses,
    includeExactMatchedOhts: request.includeExactMatchedOhts,
    practiceAreaInternalId: request.practiceArea.value,
    practiceGroupLevel: request.practiceGroupLevel.value,
    appraisalProgramYear: request.appraisalProgramYear.value,
    ohts: request.ohts,
    deviceClasses: request.deviceClasses
  }));
  const role = useReactiveVar(systemRole);

  const pageTitle = 'Appraisal View';
  const description =
    'A customized view of tables for specific Practice Areas, Practice Group Levels, ' +
    'Program Years, and optionally OHT(s) and/or device class(es) across Industry Participants in MDDAP. ' +
    'Each Table includes Practice Characterizations and Aggregate Percentages. ' +
    'Hover over a bar in any graph to see additional information. ' +
    'Click on any bar in a Practice Characterization table to see more details.';

  const breadcrumbPath = [
    { name: 'Data Reports', url: '/data-reports' },
    {
      name: 'Industry Reports',
      url: '/data-reports/industry-reports'
    },
    {
      name: 'Customization',
      url: '/data-reports/industry-reports/customization'
    },
    {
      name: pageTitle,
      url: '/data-reports/industry-reports/customization/appraisal-view'
    }
  ];
  const adjustedBreadcrumbPathItems = getAdjustedBreadcrumbPathItems(breadcrumbPath, role.Id);

  const [practiceName, setPracticeName] = useState(practiceNamePlaceHolder);

  const appraisalViewRequest = getReportsRequestObject(requestedReports);

  const {
    data,
    error,
    loading: appraisalViewLoading
  } = useQuery(GET_APPRAISAL_VIEW, {
    variables: {
      appraisalViewRequest: appraisalViewRequest
    }
  });

  const isPercentageVisible = data ? data.isPercentageVisible : null;

  const handlePracticeClick = data => {
    if (!data) {
      return;
    }

    setPracticeName(
      <p>
        <strong>{data.Acronym + ' ' + data.Level + '.' + data.NumberInLevel + ': '}</strong>
        {data.PracticeName}
      </p>
    );
  };

  const handleClickOutside = () => {
    setPracticeName(practiceNamePlaceHolder);
  };

  const downloadReportLink = async () => {
    const zip = new JSZip();

    zip.file(
      'practice-area-characterization-report.csv',
      await getPracticeAreaCharacterizationCSV(data, requestedReports)
    );

    if (data.isPercentageVisible) {
      zip.file(
        'aggregate-percentage-report.csv',
        await getAggregatePercentageCSV(data, requestedReports)
      );
    }

    zip.generateAsync({ type: 'blob' }).then(blob => {
      saveAs(blob, 'reports.zip');
    });
  };

  const downloadButton = (
    <div>
      <button
        data-testid='download-button'
        className='primary-button'
        disabled={!data}
        onClick={downloadReportLink}>
        Download Report
      </button>
    </div>
  );

  if (error) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle}>
      <Breadcrumb pathItems={adjustedBreadcrumbPathItems}>{downloadButton}</Breadcrumb>

      <PageInstructions title='Appraisal View' description={description} />
      <div className='practice-characterization--content' onClick={handleClickOutside}>
        <div className='practice-characterization--infobox'>
          <ReportInfoPanel panelName='report-info-panel__appraisal-view'>
            {practiceName}
          </ReportInfoPanel>
        </div>
        <div className='practice-characterization--graphs'>
          <IndustryReportsDescriptions
            loading={appraisalViewLoading}
            isPercentageVisible={isPercentageVisible}
          />
          <PracticeAreaGraphList
            practiceAreas={data ? data.appraisalView.PracticeAreas : []}
            requestedReports={requestedReports}
            requestedReportCount={requestedReports.length}
            handlePracticeClick={handlePracticeClick}
            isPercentageVisible={isPercentageVisible}
            loading={appraisalViewLoading}
          />
        </div>
      </div>
    </Page>
  );
};

const getReportsRequestObject = reports => {
  return reports.map(request => {
    const updatedRequest = { ...request };

    if (request.ohts && request.ohts.length > 0) {
      updatedRequest.ohts = request.ohts.map(x => x.value);
    }

    if (request.deviceClasses && request.deviceClasses.length > 0) {
      updatedRequest.deviceClasses = request.deviceClasses.map(x => x.value);
    }

    return updatedRequest;
  });
};

/** @type {React.ReactNode} */
const practiceNamePlaceHolder = 'Select a practice for more information.';

export default AppraisalViewPage;
